import React, { useRef, useMemo, useState, useEffect } from "react";

import styled from "styled-components";

import { debounce } from "utils";
import { MAX_RATE_DIALER, MIN_RATE_DIALER, LOCAL_STORAGE_NUMBERS_FILTERS_DIALER } from "const";
import { BaseInputField } from "components/base/BaseForm";
import CustomNumber from "components/client/Form/CustomNumber";
import CustomField from "components/client/Form/CustomField/CustomField";
import CustomSelfClearedForm from "components/client/Form/CustomSelfClearedForm";
import m from "definedMessages";

import { FlexboxGrid, Input, Radio, RadioGroup } from "rsuite";

export default ({ service, onChangeFilters, onChangeGroup, filter, filterModel, defaultFilter, currentDisplay, formatMessage }) => {
    const isInitialMount = useRef(true);
    const prevServiceRef = useRef(service);
    const [formValue, setFormValue] = useState({ ...defaultFilter, ...filter });
    const [formKey, setFormKey] = useState(0);

    useEffect(() => {
        const serviceChanged = prevServiceRef.current !== service;

        if (serviceChanged) {
            prevServiceRef.current = service;
            isInitialMount.current = true;
        }

        if (isInitialMount.current) {
            setFormValue({ ...defaultFilter, ...filter }); // Завантажуємо ВСЕ з localStorage
            setFormKey(prev => prev + 1);

            isInitialMount.current = false;
        } else {
            // Після першого завантаження оновлюємо тільки rates (не str)
            setFormValue(prev => ({
                ...prev,
                rate_min: filter.rate_min,
                rate_max: filter.rate_max
            }));
        }
    }, [service, filter.rate_min, filter.rate_max, filter.str]);

    const onSubmit = (data, checkedData) => {
        const checker = Object.keys(checkedData).map(i => checkedData[i].hasError).every(value => value === false);

        if (checker) {
            onChangeFilters({
                ...data,
                rate_min: parseFloat(data.rate_min) || MIN_RATE_DIALER,
                rate_max: parseFloat(data.rate_max) || MIN_RATE_DIALER
            });
        }

        return false;
    };

    const debouncedSubmit = useMemo(
        () => debounce((data, checkedData) => {
            onSubmit(data, checkedData);
        }, 1000),
        []
    );

    const handleChange = (data) => {
        setFormValue(data);
        const checkedData = filterModel.check(data);
        debouncedSubmit(data, checkedData);
    };

    return (
        <StyledGrid align="middle">
            <StyledGridItem>
                <CustomSelfClearedForm
                    key={formKey}
                    // clearOn={service}
                    model={filterModel}
                    onChange={handleChange}
                    formDefaultValue={formValue}
                    // formValue={formValue}
                    fluid
                >
                    <StyledGrid align="middle">
                        <StyledGridItem width="300px">
                            <CustomField
                                key={`str-${formKey}`}
                                accepter={Input}
                                name="str"
                                errorPlacement="topEnd"
                                placeholder={formatMessage(m.rangeNumberSearch)}
                            />
                        </StyledGridItem>

                        <StyledGridItem classPrefix="flex-item">
                            <StyledGrid align="middle">
                                <StyledGridItem>
                                    <span>{formatMessage(m.rateRange)}</span>
                                </StyledGridItem>

                                <StyledGridItem>
                                    <StyledGrid
                                        align="middle"
                                        wrap="nowrap"
                                    >
                                        <StyledGridItem>
                                            <NumberFieldWrapper>
                                                <CustomField
                                                    key={`rate_min-${formKey}`}
                                                    accepter={CustomNumber}
                                                    errorPlacement="topStart"
                                                    inline
                                                    label={formatMessage(m.from)}
                                                    max={filter.rate_max ? filter.rate_max : MIN_RATE_DIALER}
                                                    min={MIN_RATE_DIALER}
                                                    name="rate_min"
                                                    step={0.0001}
                                                // value={formValue.rate_min}
                                                />
                                            </NumberFieldWrapper>
                                        </StyledGridItem>

                                        <StyledGridItem>
                                            <NumberFieldWrapper>
                                                <CustomField
                                                    key={`rate_max-${formKey}`}
                                                    accepter={CustomNumber}
                                                    errorPlacement="bottomEnd"
                                                    inline
                                                    label={formatMessage(m.to)}
                                                    max={MAX_RATE_DIALER}
                                                    min={filter.rate_min ? filter.rate_min : MIN_RATE_DIALER}
                                                    name="rate_max"
                                                    step={0.0001}
                                                // value={formValue.rate_max}
                                                />
                                            </NumberFieldWrapper>
                                        </StyledGridItem>

                                    </StyledGrid>
                                </StyledGridItem>

                            </StyledGrid>
                        </StyledGridItem>
                    </StyledGrid>
                </CustomSelfClearedForm>
            </StyledGridItem>

            <StyledGridItem>
                <RadioGroup
                    onChange={onChangeGroup}
                    defaultValue={currentDisplay}
                    name="radioList"
                    inline
                >
                    <Radio value="trunk_number" disabled={false}>{formatMessage(m.numbers)}</Radio>
                    <Radio value="price_range" disabled={false}>{formatMessage(m.ranges)}</Radio>
                    <Radio value="subdestination" disabled={false}>{formatMessage(m.subdestination)}</Radio>
                </RadioGroup>
            </StyledGridItem>

        </StyledGrid>
    );
};

const StyledGrid = styled(FlexboxGrid)`
    && {
        flex-wrap: ${props => props.wrap || "wrap"};
        margin-bottom: ${props => props.vSpace || "-20px"};
    }
`;

const StyledGridItem = styled(FlexboxGrid.Item)`
    padding-bottom: ${props => props.vSpace || "20px"};
    padding-right: ${props => props.hSpace || "20px"};
    position: relative;

    &&:last-child {
        padding-right: 0;
    }
    
    && {
        flex-shrink: 1;
    }

    .rtl & {
        padding-left: ${props => props.hSpace || "20px"};
        padding-right: 0;

        &&:last-child {
            padding-left: 0;
        }
    }
`;

const NumberFieldWrapper = styled.div`
    &&& {
        .rs-input-number {
            width: 100%;
            border: 1px solid var(--color-grey);
        }
        .rs-form-control-wrapper {
            max-width: 112px;
        }
    }
`;